import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Box, Typography, Button, InputLabel, Stack, TextField, Divider, Switch, Tab, Tabs, Modal, Autocomplete } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { MembersStoreContext } from "../../Store/MembersStore";
import { useMarkForRefresh } from "../../Hooks/useMarkForRefresh";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { addOrUpdateUser, deleteUser, changePasswordForMember, getUserById } from "../../Requests/UserService.ts";
import AvatarDropzone from "../StyledComponents/AvatarDropzone/AvatarDropzone";
import {
  MaterialReactTable,
  useMaterialReactTable
} from 'material-react-table';
import { eventsForUser } from "../../Requests/EventService.ts";
import { getChipForMemberStatus } from "./Members";
import { DeleteForever, Password, ArrowBack, FileDownload } from "@mui/icons-material";
import { exportTableToCsv, exportDataToCsv } from "../../Utils/exportTableToCsv.js";
import { MEMBER_STATUS, MEMBER_TYPE, MEMBER_TYPE_CASES, MEMBER_CATEGORY_CASES } from "../../Models/StateConstants.ts";
import { Member } from "../../Models/Member.ts";
import CircularProgressView from "../StyledComponents/CircularProgressView.jsx";


const stripHtmlToText = (value) => {
  if (value === null || value === undefined) return ""
  if (typeof value !== "string") return String(value)
  if (value.trim().length === 0) return ""

  try {
    const doc = new DOMParser().parseFromString(value, "text/html")
    return doc?.body?.textContent ?? ""
  } catch (e) {
    return value
  }
}


const APP_BAR_HEIGHT = 64

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4
}


const COLUMNS = [
  {
    accessorKey: 'title',
    header: 'Event',
    Cell: ({ renderedCellValue, row }) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <img
          alt="avatar"
          height={42}
          width={64}
          src={row.original.image}
          loading="lazy"
        />
        <span>{renderedCellValue}</span>
      </Box>
    )
  },
  {
    accessorFn: (originalRow) => new Date(originalRow.startDate),
    accessorKey: 'startDate',
    header: 'Event Date',
    Cell: ({ cell }) => dayjs(cell.getValue()).format('DD MMM YYYY'),
  },
]


export default function MemberInfo() {

  const navigate = useNavigate()
  const { memberid } = useParams()
  const markForRefresh = useMarkForRefresh(MembersStoreContext)
  const [isLoading, setIsLoading] = useState(true)

  const [avatar, setAvatar] = useState()
  const [name, setName] = useState()
  const [surname, setSurname] = useState()
  const [email, setEmail] = useState()
  const [phoneNumber, setPhoneNumber] = useState()
  const [organization, setOrganization] = useState()
  const [position, setPosition] = useState()
  const [membershipValidStart, setMembershipValidStart] = useState()
  const [membershipValidUntil, setMembershipValidUntil] = useState()
  const [about, setAbout] = useState()
  const [isNew, setIsNew] = useState()
  const [hidden, setHidden] = useState()
  const [isSuspend, setIsSuspend] = useState()
  const [password, setPassword] = useState("")
  const [memberType, setMemberType] = useState()
  const [memberStatus, setMemberStatus] = useState()
  const [memberCategory, setMemberCategory] = useState()
  const [newMembersOrder, setNewMembersOrder] = useState()
  const [passwordSet, setPasswordSet] = useState()
  const [createdAt, setCreatedAt] = useState()
  const [updatedAt, setUpdatedAt] = useState()
  const [gender, setGender] = useState("")

  const [hidePasswordField, setHidePasswordField] = useState(false)
  useEffect(() => {
    setHidePasswordField(memberType === MEMBER_TYPE.DRAFT)
  }, [memberType])

  const [open, setOpen] = useState(false)

  const [selectedTab, setSelectedTab] = useState(0)

  const headerRef = useRef(null)
  const [headerHeight, setHeaderHeight] = useState(0)

  useEffect(() => {
    if (!headerRef.current) return

    const updateHeight = () => {
      setHeaderHeight(headerRef.current?.offsetHeight ?? 0)
    }

    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(headerRef.current)

    return () => observer.disconnect()
  }, [])

  const HEADER_BOTTOM_OFFSET = APP_BAR_HEIGHT + headerHeight

  const handleSelectedTab = (event, newValue) => {
    setSelectedTab(newValue)
  }

  const [tableData, setTableData] = useState([])
  const columns = useMemo(() => COLUMNS, [])
  const data = useMemo(() => tableData, [tableData])

  const handleDeleteUser = useCallback(async () => {
    await deleteUser(memberid)
    markForRefresh()
    navigate("/members")
  }, [memberid, markForRefresh, navigate])

  const fetchData = useCallback(async () => {
    const user = await getUserById(memberid)
    setAvatar(user.avatar)
    setName(user.name)
    setSurname(user.surname)
    setEmail(user.email)
    setPhoneNumber(user.phoneNumber)
    setOrganization(user.organization)
    setPosition(user.position)
    setMembershipValidStart(user.membershipValidStart)
    setMembershipValidUntil(user.membershipValidUntil)
    // About больше не редактируем как HTML — приводим к plain text.
    setAbout(stripHtmlToText(user.about))
    setIsNew(user.isNew)
    setHidden(user.hidden)
    setIsSuspend(user.isSuspend)
    setMemberType(user.memberType)
    setMemberStatus(user.memberStatus)
    setMemberCategory(user.memberCategory)
    setNewMembersOrder(user.newMembersOrder)
    setPasswordSet(user.passwordSet)
    setCreatedAt(user.createdAt)
    setUpdatedAt(user.updatedAt)
    setGender(user.gender)

    const userEvents = await eventsForUser(memberid)
    setTableData(userEvents)

    setIsLoading(false)
  }, [memberid])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const getMemberForExport = useCallback(() => ({
    objectId: memberid,
    avatar,
    name,
    surname,
    email,
    phoneNumber,
    organization,
    position,
    membershipValidStart,
    membershipValidUntil,
    about,
    isNew,
    hidden,
    isSuspend,
    memberType,
    memberStatus,
    memberCategory,
    newMembersOrder,
    passwordSet,
    createdAt,
    updatedAt,
    gender,
  }), [
    about,
    avatar,
    createdAt,
    email,
    gender,
    hidden,
    isNew,
    isSuspend,
    memberCategory,
    memberStatus,
    memberType,
    memberid,
    membershipValidStart,
    membershipValidUntil,
    name,
    newMembersOrder,
    organization,
    passwordSet,
    phoneNumber,
    position,
    surname,
    updatedAt,
  ])

  const table = useMaterialReactTable({
    columns,
    data,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enablePagination: false,
    enableColumnActions: false,
    enableSorting: false,
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        border: "1px solid",
        color: "grey.300"
      }
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: 'calc(100vh - 300px)',
        overflow: 'auto'
      }
    }
  })

  const handleSaveChanges = async () => {
    let user = new Member()
    user.objectId = memberid
    user.name = name
    user.surname = surname
    user.email = email
    user.phoneNumber = phoneNumber
    user.organization = organization
    user.position = position
    user.membershipValidStart = dayjs(membershipValidStart).toDate()
    user.membershipValidUntil = dayjs(membershipValidUntil).toDate()
    user.about = about
    user.memberType = memberType
    user.hidden = hidden
    user.isNew = isNew
    user.newMembersOrder = isNew ? newMembersOrder : null
    user.isSuspend = isSuspend
    user.memberCategory = memberCategory
    user.gender = gender
    await changePasswordIfNeeded()
    await addOrUpdateUser(user, avatar)
    markForRefresh()
    navigate("/members")
  }

  const changePasswordIfNeeded = async () => {
    if (password.length > 0) {
      await changePasswordForMember(memberid, password)
    }
  }

  const handleIsNewMember = async (event) => {
    setIsNew(event)
  }

  const handleHiddenMember = async (event) => {
    setHidden(!event)
  }

  const handleChangePassword = async () => {
    await changePasswordForMember(memberid, password)
    setOpen(false)
    alert("Password successfully changed")
  }

  const handleSuspendMember = async () => {
    const suspend = isSuspend === undefined ? true : !isSuspend
    setIsSuspend(suspend)
  }

  const changeMemberStatus = useCallback((isSuspend) => {
    if (isSuspend) {
      return MEMBER_STATUS.SUSPENDED
    } else {
      if (passwordSet) {
        return MEMBER_STATUS.ACTIVE
      } else {
        return MEMBER_STATUS.INACTIVE
      }
    }
  }, [passwordSet])

  useEffect(() => {
    const status = changeMemberStatus(isSuspend)
    setMemberStatus(status)
  }, [isSuspend, changeMemberStatus])

  const loaderView = <CircularProgressView />

  const contentView = (
    <Box>
      <Box
        ref={headerRef}
        sx={{
          position: "sticky",
          top: APP_BAR_HEIGHT,
          backgroundColor: "background.paper",
          zIndex: 100
        }}
      >
        <Box
          paddingTop={2}
          paddingBottom={2}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            gap: 1,
            width: "calc(100% - 280px)",
            paddingRight: 2
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate("/members")}
              sx={{ minWidth: "auto", alignSelf: "flex-start" }}
            >
              Back
            </Button>
            <Typography
              variant="h4"
              sx={{
                maxWidth: "600px"
              }}
            >
              {name + " " + surname}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            onClick={() => handleSaveChanges()}
          >
            Save Changes
          </Button>
        </Box>
        <Divider />
        <Box sx={{ width: "calc(100% - 280px)", paddingRight: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={handleSelectedTab}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Member Details" />
            <Tab label="Membership" />
            <Tab label="Event History" />
          </Tabs>
          <Divider />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between"
        }}
      >
        <Box
          width="calc(100% - 280px)"
          sx={{
            paddingRight: 2
          }}
        >
          <Box
            width="600px"
            hidden={selectedTab !== 0}
          >
            <AvatarDropzone
              title={"Member Avatar"}
              avatarUrl={avatar}
              callbackFile={(file) => { setAvatar(file) }}
            />

            <Stack
              spacing={1}
              marginTop={4}
            >
              <InputLabel>{"Member Type"}</InputLabel>
              <Autocomplete
                options={MEMBER_TYPE_CASES}
                renderInput={(params) => <TextField {...params} />}
                value={memberType}
                onChange={(event) => setMemberType(event.target.textContent)}
              />
            </Stack>

            <Stack
              spacing={1}
              marginTop={4}
            >
              <InputLabel>{"First Name"}</InputLabel>
              <TextField
                placeholder="First Name"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </Stack>

            <Stack
              spacing={1}
              marginTop={4}
            >
              <InputLabel>{"Last Name"}</InputLabel>
              <TextField
                placeholder="Last Name"
                value={surname}
                onChange={(event) => setSurname(event.target.value)}
              />
            </Stack>

            <Stack
              spacing={1}
              marginTop={4}
            >
              <InputLabel>{"Email Address"}</InputLabel>
              <TextField
                placeholder="Email Address"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Stack>

            <Stack
              spacing={1}
              marginTop={4}
            >
              <InputLabel>{"Phone Number"}</InputLabel>
              <TextField
                placeholder="Phone Number"
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value)}
              />
            </Stack>

            <Stack
              spacing={1}
              marginTop={4}
            >
              <InputLabel>{"Organization"}</InputLabel>
              <TextField
                placeholder="Organization"
                value={organization}
                onChange={(event) => setOrganization(event.target.value)}
              />
            </Stack>

            <Stack
              spacing={1}
              marginTop={4}
            >
              <InputLabel>{"Position"}</InputLabel>
              <TextField
                placeholder="Position"
                value={position}
                onChange={(event) => setPosition(event.target.value)}
              />
            </Stack>

            <Stack
              spacing={1}
              marginTop={4}
            >
              <InputLabel>{"Gender"}</InputLabel>
              <TextField
                select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </TextField>
            </Stack>

            <Box
              hidden={hidePasswordField}
            >
              <Stack
                spacing={1}
                marginTop={4}
              >
                <InputLabel>{"Temporary password"}</InputLabel>
                <TextField
                  placeholder="Temporary password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Stack>
            </Box>

            <Stack spacing={1} marginTop={4}>
              <InputLabel>About</InputLabel>
              <TextField
                placeholder="About"
                value={about ?? ""}
                onChange={(event) => setAbout(event.target.value)}
                multiline
                minRows={6}
              />
            </Stack>
          </Box>

          <Box
            width="600px"
            hidden={selectedTab !== 1}
          >
            <Stack
              spacing={1}
              marginTop={4}
            >
              <InputLabel>{"Member Category"}</InputLabel>
              <Autocomplete
                options={MEMBER_CATEGORY_CASES}
                renderInput={(params) => <TextField {...params} />}
                value={memberCategory}
                onChange={(event) => setMemberCategory(event.target.textContent)}
              />
            </Stack>

            <Stack
              spacing={1}
              marginTop={4}
            >
              <InputLabel>{"Membership Start Date"}</InputLabel>
              <DatePicker
                format="DD/MM/YYYY"
                value={membershipValidStart ? dayjs(membershipValidStart) : undefined}
                onChange={(newValue) => setMembershipValidStart(newValue)}
              />
            </Stack>

            <Stack
              spacing={1}
              marginTop={4}
            >
              <InputLabel>{"Membership Renewal Date"}</InputLabel>
              <DatePicker
                format="DD/MM/YYYY"
                value={membershipValidUntil ? dayjs(membershipValidUntil) : undefined}
                onChange={(newValue) => setMembershipValidUntil(newValue)}
              />
            </Stack>

            <Box
              spacing={1}
              marginTop={4}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleSuspendMember()}
              >
                {
                  isSuspend ? "Unsuspend Membership" : "Suspend Membership"
                }
              </Button>
            </Box>
          </Box>

          <Box
            marginTop={4}
            hidden={selectedTab !== 2}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                size="small"
                startIcon={<FileDownload />}
                onClick={() => {
                  const done = exportTableToCsv(table, memberid ? `member-${memberid}-events` : "member-events");
                  if (!done) alert("No data to export.");
                }}
              >
                Export CSV
              </Button>
            </Box>
            <MaterialReactTable
              table={table}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "280px",
            position: "fixed",
            right: 0,
            top: APP_BAR_HEIGHT + 1,
            bottom: 0,
            overflowY: "auto",
            backgroundColor: "background.paper",
            borderLeft: "1px solid",
            borderColor: "grey.200",
            zIndex: 110
          }}
        >
          <Divider orientation="vertical" />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              padding: 2
            }}
          >
            <Button
              size="small"
              startIcon={<FileDownload />}
              onClick={() =>
                exportDataToCsv([getMemberForExport()], memberid ? `member-${memberid}` : "member", {
                  preferredKeys: ["firstName", "lastName"],
                  keyMap: { firstName: "name", lastName: "surname" },
                  omitKeys: ["name", "surname"],
                })
              }
              sx={{ mb: 2, alignSelf: "flex-start" }}
            >
              Export CSV
            </Button>
            <Box
              marginTop={1}
              width="100%"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <InputLabel>{"Member Status"}</InputLabel>
              {getChipForMemberStatus(memberStatus)}
            </Box>

            <Box
              marginTop={1}
              width="100%"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <InputLabel>{"Visibility"}</InputLabel>
              <Switch
                checked={!hidden}
                onChange={(event) => handleHiddenMember(event.target.checked)}
              />
            </Box>

            <Box
              marginTop={1}
              marginBottom={2}
              width="100%"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <InputLabel>{"Whats's New"}</InputLabel>
              <Switch
                checked={isNew}
                onChange={(event) => handleIsNewMember(event.target.checked)}
              />
            </Box>
            <Divider />

            <Box
              marginTop={2}
              width="100%"
              display="flex"
              justifyContent="space-between"
            >
              <InputLabel>{"Date Created"}</InputLabel>
              <InputLabel>{dayjs(createdAt).format('DD/MMM/YYYY')}</InputLabel>
            </Box>

            <Box
              marginTop={1}
              marginBottom={2}
              width="100%"
              display="flex"
              justifyContent="space-between"
            >
              <InputLabel>{"Last Update"}</InputLabel>
              <InputLabel>{dayjs(updatedAt).format('DD/MMM/YYYY')}</InputLabel>
            </Box>

            <Divider />

            <Box
              sx={{
                marginTop: 2,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1
              }}
            >
              {
                !hidePasswordField
                  ? <Button
                    variant="outlined"
                    onClick={() => setOpen(true)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 1
                      }}
                    >
                      <Password />
                      Change Password
                    </Box>
                  </Button>
                  : <></>
              }

              <Button
                variant="outlined"
                color="error"
                onClick={() => handleDeleteUser()}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1
                  }}
                >
                  <DeleteForever />
                  Delete Member
                </Box>
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Modal
        open={open}
      >
        <Box
          sx={style}
        >
          <Stack
            spacing={1}
          >
            <InputLabel>{"New Password"}</InputLabel>
            <TextField
              placeholder="New Password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Stack>

          <Box
            sx={{
              marginTop: 2,
              width: "100%",
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <Button
              color="error"
              onClick={() => setOpen(false)}
            >
              Close
            </Button>
            <Button
              disabled={!(password.length > 0)}
              onClick={() => handleChangePassword()}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  )

  return (
    <>
      {isLoading ? loaderView : contentView}
    </>
  )
}
